import styled from 'styled-components';
import { Row, Section, SectionHeader } from '../../../../../styling/FormStyling';
import TextRow, { RowLabel, RowValue } from '../../components/TextRow';
import BasicPopover from '../../../../../components/popover/Popover';
import Person from '../../../../../components/person/Person';
import ContactCard from '../../../../../components/contact-card/ContactCard';
import { MeterTaskDetailsContext, MeterTaskDetailsDispatchContext } from '../MeterTaskDetailsStep';
import { TaskDetailsContext, TaskDetailsDispatchContext, StyledSectionContent } from '../TaskDetailsStep';
import { WorkTaskDTOExtended } from '../../../../../models/TaskType';
import { convertHoursAndMinutesToMinutes, convertMinutesToHoursAndMinutes } from '../../../../../utils/timeHandling';
import SimpleTime from '../../../../../components/simple-time/SimpleTime';
import DepartmentDropdown from '../../../../../components/department-dropdown/DepartmentDropdown';
import { CloseIcon } from '../../../../../assets/icons/CloseIcon';
import ProjectNumberDropdown, {
  MappedProjectNumber
} from '../../../../../components/project-number-dropdown/ProjectNumberDropdown';
import { CheckmarkIcon } from '../../../../../assets/icons/CheckmarkIcon';
import IconButton from '../../../../../components/icon-button/IconButton';
import { CustomerAppointmentDTO, DepartmentDTO } from '../../../../../api/api';
import { EditIcon } from '../../../../../assets/icons/EditIcon';
import { ColumnFlex3 } from '../../../../../styling/Styling';
import { changeToEndOfDay, dateStringsAreEqual, formatTimeAndDateString } from '../../../../../utils/dateHandling';
import { useContext } from 'react';
import { getEnumDisplayValue } from '../../../../../utils/enumUtils';
import { useConfirmationDialog } from '../../../../../hooks/useConfirmationDialog';
import { DialogBody } from '../../../../../stateManagement/reducers/confirmDialogReducer';
import TimeRegistrationService from '../../../../../services/TimeRegistrationService';
import { getWorkPermitTaskTypeId } from '../../../../../utils/constants';
import DateTimePicker from '../../../../../components/date-time-picker/DateTimePicker';
import dayjs from 'dayjs';

const body = {
  headerText: `Vil du ændre projektnummeret?`,
  bodyText: `Der er timer og/eller varer registreret på opgaven.`,
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Bekræft'
} as DialogBody;

interface Props {
  editable?: boolean;
  task?: WorkTaskDTOExtended;
  customerAppointment?: CustomerAppointmentDTO;
  saveNewProjectNumber?: () => void;
  cancelChanges: () => void;
  workInvoice?: number;
  workInvoiceProjectNumber?: string;
  isDirty: boolean;
  toggleFieldIsDirty: (key: string, value: boolean) => void;
  isMeterTask: boolean;
}

export const Details = (props: Props) => {
  const {
    editable,
    task,
    saveNewProjectNumber,
    cancelChanges,
    workInvoice,
    workInvoiceProjectNumber,
    isDirty,
    toggleFieldIsDirty,
    isMeterTask
  } = props;

  const dispatchTaskDetails = useContext(isMeterTask ? MeterTaskDetailsDispatchContext : TaskDetailsDispatchContext);
  const {
    department,
    customerAppointment,
    remainingTime,
    isProjectNumberEditable,
    projectNumberObject,
    externalProjects,
    isLoadingProjectNumbers,
    projectNumberAndName,
    projectManager,
    earliestStartDate,
    deadline
  } = useContext(isMeterTask ? MeterTaskDetailsContext : TaskDetailsContext);

  const { getConfirmation } = useConfirmationDialog();

  const isWorkPermit = task?.taskType?.id === getWorkPermitTaskTypeId();

  const getTimeText = (hours: number, minutes: number) => {
    const hoursWord = hours === 1 ? 'time' : 'timer';
    const minuteWord = minutes === 1 ? 'minut' : 'minutter';

    if (hours === 0) return `${minutes} ${minuteWord}`;
    if (minutes === 0) return `${hours} ${hoursWord}`;

    return `${hours} ${hoursWord} og ${minutes} ${minuteWord}`;
  };

  const handleStartDateChange = (date: string | undefined) => {
    if (!!date) {
      const dateAsDate = new Date(date);
      const earliestStartDateAsDate = new Date(task?.earliestStartDate ?? '');

      dispatchTaskDetails({ type: 'set-earliest-start-date', payload: date });
      toggleFieldIsDirty('earliestStartDate', dateAsDate.toString() !== earliestStartDateAsDate.toString());
    }
  };

  const handleDeadlineChange = (date: string | undefined) => {
    if (!!date && task?.deadline) {
      // A work permit's end time is set by the user, so it must not be moved to the end of the day
      const normalizedDeadline = isWorkPermit ? date : changeToEndOfDay(new Date(date)).toISOString();
      dispatchTaskDetails({ type: 'set-deadline', payload: normalizedDeadline });
      toggleFieldIsDirty('deadlineDate', !dateStringsAreEqual(normalizedDeadline, task?.deadline));
    }
  };

  const handleFollowUpDateChange = (date: string | undefined) => {
    if (date) {
      dispatchTaskDetails({ type: 'set-follow-up-date', payload: date });
      toggleFieldIsDirty('followUpDate', !dateStringsAreEqual(date, task?.followUpDate ?? ''));
    }
  };

  const handleRemainingTimeChange = (minutes: string | undefined) => {
    const parsedMinutes = minutes ? parseInt(minutes) : 0;
    dispatchTaskDetails({ type: 'set-remaining-time', payload: parsedMinutes });
    toggleFieldIsDirty('remainingTimeInMinutes', parsedMinutes !== remainingTime);
  };

  const handleChangeDepartment = (department: DepartmentDTO) => {
    toggleFieldIsDirty('department', department !== task?.assignedToDepartment);
    dispatchTaskDetails({ type: 'set-department', payload: department });
  };

  const handleProjectNumberChange = (value: MappedProjectNumber) => {
    if (!value || !task?.id) return;

    TimeRegistrationService.checkIfWorkTaskHasConsumedItemsOrTimeRegistrations(parseInt(task?.id)).then(
      async (showWarning) => {
        if (showWarning) {
          const confirmation = await getConfirmation(body);

          if (confirmation === 'confirm') {
            dispatchTaskDetails({ type: 'set-project-number-object', payload: value });
            toggleFieldIsDirty('projectNumber', task?.projectNumber !== value?.value.id);
          }
        } else {
          dispatchTaskDetails({ type: 'set-project-number-object', payload: value });
          toggleFieldIsDirty('projectNumber', task?.projectNumber !== value?.value.id);
        }
      }
    );
  };

  return (
    <Section>
      <SectionHeader>Detaljer</SectionHeader>
      <StyledSectionContent>
        <StyledColumnFlex>
          {task?.alarmType && <TextRow label="Alarmtype" value={getEnumDisplayValue(task.alarmType)} />}
          {isWorkPermit ? (
            editable ? (
              <>
                <DateTimePicker
                  onChange={(date) => date?.isValid() && handleStartDateChange(date.toISOString())}
                  value={earliestStartDate ? dayjs(earliestStartDate) : null}
                  label="Start tidspunkt"
                  disabled={!!customerAppointment}
                />
                <DateTimePicker
                  onChange={(date) => date?.isValid() && handleDeadlineChange(date.toISOString())}
                  value={deadline ? dayjs(deadline) : null}
                  label="Slut tidspunkt"
                  disabled={!!customerAppointment}
                  minDateTime={earliestStartDate ? dayjs(earliestStartDate) : undefined}
                />
              </>
            ) : (
              <>
                <TextRow label="Start tidspunkt" value={formatTimeAndDateString(task?.earliestStartDate ?? '')} />
                <TextRow label="Slut tidspunkt" value={formatTimeAndDateString(task?.deadline ?? '')} />
              </>
            )
          ) : (
            <>
              {!editable ? (
                <TextRow
                  type="number"
                  label="Normtid"
                  value={
                    task?.normTimeMin
                      ? getTimeText(
                          convertMinutesToHoursAndMinutes(task?.normTimeMin).hours,
                          convertMinutesToHoursAndMinutes(task?.normTimeMin).minutes
                        )
                      : '-'
                  }
                />
              ) : (
                <SimpleTime
                  label="Normtid"
                  initialHours={convertMinutesToHoursAndMinutes(task?.normTimeMin ?? 0).hours}
                  initialMinutes={convertMinutesToHoursAndMinutes(task?.normTimeMin ?? 0).minutes}
                  disabled
                />
              )}

              {!editable ? (
                <TextRow
                  type="number"
                  label="Resterende tid"
                  onChange={handleRemainingTimeChange}
                  value={
                    task?.remainingTimeMin
                      ? getTimeText(
                          convertMinutesToHoursAndMinutes(task?.remainingTimeMin).hours,
                          convertMinutesToHoursAndMinutes(task?.remainingTimeMin).minutes
                        )
                      : '-'
                  }
                />
              ) : (
                <SimpleTime
                  label="Resterende tid"
                  initialHours={convertMinutesToHoursAndMinutes(task?.remainingTimeMin ?? 0).hours}
                  initialMinutes={convertMinutesToHoursAndMinutes(task?.remainingTimeMin ?? 0).minutes}
                  onTimeChange={(hours, minutes) =>
                    handleRemainingTimeChange(convertHoursAndMinutesToMinutes(hours, minutes).toString())
                  }
                />
              )}
              <TextRow
                inputMode={editable}
                type="date"
                label="Startdato"
                value={task?.earliestStartDate ?? ''}
                onChange={handleStartDateChange}
                disabled={!!customerAppointment}
              />
              <TextRow
                inputMode={editable}
                type="date"
                label="Slutdato"
                value={task?.deadline ?? ''}
                onChange={handleDeadlineChange}
                disabled={!!customerAppointment}
              />
              <TextRow
                inputMode={editable}
                type="date"
                label="Opfølgningsdato"
                value={task?.followUpDate ?? ''}
                onChange={handleFollowUpDateChange}
              />
            </>
          )}
          <TextRow
            inputMode={false}
            disabled
            label="Work Ordre Nr"
            value={task?.dynamicsWorkOrderNumber ?? '-'}
          />
          <TextRow
            inputMode={editable}
            disabled
            label="Spænding"
            value={task?.primaryVoltage ? task?.primaryVoltage / 1000 + ' kV' : '-'}
          />
          {editable ? (
            <DepartmentDropdown selectDepartment={handleChangeDepartment} value={department?.departmentId} />
          ) : (
            <TextRow label="Send til" value={task?.assignedToDepartment?.name ? task?.assignedToDepartment.name : '-'} />
          )}

          <Row>
            {(editable || isProjectNumberEditable) && !isLoadingProjectNumbers && (
              <>
                <ProjectNumberDropdown
                  value={projectNumberObject}
                  projectNumberChange={(value: any) => handleProjectNumberChange(value)}
                  options={externalProjects ?? []}
                  isLoadingProjectNumbers={isLoadingProjectNumbers}
                  disableClearable
                  required
                />
                {isProjectNumberEditable && !editable && (
                  <ButtonContainer>
                    <StyledIconButton onClick={saveNewProjectNumber} disabled={!isDirty}>
                      <CheckmarkIcon size="18px" />
                    </StyledIconButton>
                    <StyledIconButton onClick={cancelChanges}>
                      <CloseIcon size="16px" />
                    </StyledIconButton>
                  </ButtonContainer>
                )}
              </>
            )}
            {!isProjectNumberEditable && !editable && (
              <ProjectNumberContainer>
                <TextRow
                  inputMode={editable}
                  disabled
                  label={workInvoice ? 'Oprindeligt projektnummer' : 'Projektnummer'}
                  value={projectNumberAndName ?? ''}
                />
                {!workInvoice && saveNewProjectNumber && (
                  <StyledIconButton
                    onClick={() => {
                      dispatchTaskDetails({ type: 'set-is-project-number-editable', payload: true });
                    }}
                  >
                    <EditIcon size="20px" />
                  </StyledIconButton>
                )}
              </ProjectNumberContainer>
            )}
          </Row>
          {task?.projectNumber && (
            <StyledRowBase>
              <RowLabel>Projekt Leder</RowLabel>
              <RowValue>
                {projectManager?.name ? (
                  <BasicPopover
                    buttonElement={<Person name={projectManager.name} />}
                    popoverElement={<ContactCard email={projectManager.email} />}
                  />
                ) : (
                  'Fandt ikke projektleder'
                )}
              </RowValue>
            </StyledRowBase>
          )}
          {workInvoice && (
            <Row>
              <TextRow
                inputMode={false}
                disabled
                label={'Skadevolder/regningsarbejde projektnummer'}
                value={workInvoiceProjectNumber ?? '-'}
              />
            </Row>
          )}
        </StyledColumnFlex>
      </StyledSectionContent>
    </Section>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  padding: 10px;
`;

const StyledIconButton = styled(IconButton)`
  padding: 10px 10px;
`;

const ProjectNumberContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledRowBase = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;

export const StyledColumnFlex = styled(ColumnFlex3)`
  justify-content: space-between;
  width: 50%;
`;
